// src/components/header/info-cliente.ts
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import Swal from 'sweetalert2';
import QRCode from 'qrcode';
import { setSuppressLogoutAlert, logout, resetAuthSystem } from '../../services/auth-service';
import { ClientAccountService, ClientData } from '../../services/client-account.service';

@customElement('info-cliente')
export class InfoCliente extends LitElement {
  @property({ type: String }) usuario = '';
  @property({ type: Boolean, reflect: true }) icononly = false;
  @property({ type: Boolean, reflect: true }) expanded = false;
  @state() private working = false;
  @state() private clientData: ClientData | null = null;
  @state() private qrCodeUrl: string = '';

  createRenderRoot() { return this; }

  private get labName(): string {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return '';
      const u = JSON.parse(raw);
      return (u?.nombre || u?.usuario_nombre || '').toString();
    } catch { return ''; }
  }

  private get isClienteAdmin(): boolean {
    try {
      const role = (localStorage.getItem('role') || '').toLowerCase();
      if (role === 'cliente_admin' || role === 'administrador cliente') return true;
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userRol = (user?.rol || user?.rol_nombre || '').toLowerCase();
      return userRol === 'cliente_admin' || userRol === 'administrador cliente';
      return userRol === 'cliente_admin' || userRol === 'administrador cliente';
    } catch { return false; }
  }

  private get hasQR(): boolean {
    if (this.clientData?.qr) return true;
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return !!u.qr;
    } catch { return false; }
  }



  private getLogoutSwalConfig(): any {
    return {
      title: '¿Cerrar sesión?',
      html: `
        <div style="text-align: center; padding: 0.5rem 0;">
          <p style="margin: 0.5rem 0; color: #6c757d; font-size: 0.95rem;">
            ¿Estás seguro de que deseas cerrar tu sesión?
          </p>
        </div>
      `,
      icon: 'warning',
      background: '#ffffff',
      color: '#212529',
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonColor: '#6c757d',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      focusCancel: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCancelButton: true,
      buttonsStyling: true,
      customClass: {
        confirmButton: 'swal-logout-confirm',
        cancelButton: 'swal-logout-cancel',
        popup: 'swal-logout-popup',
        title: 'swal-logout-title',
        htmlContainer: 'swal-logout-html'
      },
      didOpen: () => {
        const style = document.createElement('style');
        style.textContent = `
          .swal-logout-popup {
            border-radius: 16px !important;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15) !important;
          }
          .swal-logout-title {
            font-size: 1.5rem !important;
            font-weight: 600 !important;
            color: #212529 !important;
            margin-bottom: 1rem !important;
          }
          .swal-logout-html {
            font-size: 0.95rem !important;
            color: #6c757d !important;
            line-height: 1.5 !important;
          }
          .swal-logout-confirm {
            background-color: #dc3545 !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 0.6rem 1.5rem !important;
            font-weight: 600 !important;
            transition: all 0.3s ease !important;
          }
          .swal-logout-confirm:hover {
            background-color: #c82333 !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3) !important;
          }
          .swal-logout-cancel {
            background-color: #e9ecef !important;
            color: #6c757d !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 0.6rem 1.5rem !important;
            font-weight: 600 !important;
            transition: all 0.3s ease !important;
          }
          .swal-logout-cancel:hover {
            background-color: #dee2e6 !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          }
        `;
        document.head.appendChild(style);
      }
    };
  }

  private async loadClientData() {
    try {
      const res = await ClientAccountService.getClientData();
      if (res.success && res.data) {
        this.clientData = res.data;

        // Fallback: Si la API no devuelve el QR, intentar obtenerlo del localStorage (del login)
        if (!this.clientData.qr) {
          try {
            const localUser = JSON.parse(localStorage.getItem('user') || '{}');
            if (localUser && localUser.qr) {
              console.log('Recuperando QR desde localStorage');
              this.clientData = { ...this.clientData, qr: localUser.qr };
            }
          } catch (err) {
            console.error('Error leyendo user de localStorage', err);
          }
        }
      }
    } catch (e) {
      console.error('Error loading client data', e);
    }
  }

  private async showClientData() {
    if (!this.clientData) {
      this.working = true;
      await this.loadClientData();
      this.working = false;
    }

    if (!this.clientData) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los datos del cliente.',
        confirmButtonColor: '#3b6fbf'
      });
      return;
    }

    const d = this.clientData;

    // Generar HTML con estilos inline para asegurar visualización correcta
    const contentHtml = `
      <div style="text-align: left; font-size: 0.95rem; line-height: 1.8; color: #333;">
        <div style="margin-bottom: 8px;"><strong style="color: #555;">Razón Social:</strong> <span style="font-weight: 500;">${d.razon_social}</span></div>
        <div style="margin-bottom: 8px;"><strong style="color: #555;">Cód. Farmacia:</strong> <span style="font-weight: 500;">${d.cod_farmacia}</span></div>
        <div style="margin-bottom: 8px;"><strong style="color: #555;">Dirección:</strong> <span style="font-weight: 500;">${d.direccion}</span></div>
        <div style="margin-bottom: 8px;"><strong style="color: #555;">Localidad:</strong> <span style="font-weight: 500;">${d.localidad}</span></div>
        <div style="margin-bottom: 8px;"><strong style="color: #555;">Provincia:</strong> <span style="font-weight: 500;">${d.provincia}</span></div>
        <div style="margin-bottom: 8px;"><strong style="color: #555;">Teléfono:</strong> <span style="font-weight: 500;">${d.telefono}</span></div>
        <div style="margin-bottom: 8px;"><strong style="color: #555;">Email:</strong> <span style="font-weight: 500;">${d.email}</span></div>
      </div>
    `;

    Swal.fire({
      title: 'Datos del Cliente',
      html: contentHtml,
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#3b6fbf',
      background: '#fff',
      customClass: {
        popup: 'client-data-popup'
      },
      didOpen: () => {
        // Asegurar que el popup tenga borde redondeado
        const popup = document.querySelector('.client-data-popup') as HTMLElement;
        if (popup) popup.style.borderRadius = '12px';
      }
    });
  }

  private async handleShowQR() {
    if (!this.clientData) {
      this.working = true;
      await this.loadClientData();
      this.working = false;
    }

    if (!this.clientData || !this.clientData.qr) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin código QR',
        text: 'No se encontró un código QR asociado a tu cuenta.',
        confirmButtonColor: '#ff6b35'
      });
      return;
    }

    try {
      this.working = true;
      this.qrCodeUrl = await QRCode.toDataURL(this.clientData.qr, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      const result = await Swal.fire({
        title: 'Tu Código QR',
        html: `
          <div style="display: flex; justify-content: center; align-items: center; padding: 10px;">
            <img src="${this.qrCodeUrl}" alt="QR Code" style="width: 250px; height: 250px; border-radius: 8px; border: 1px solid #ddd;" />
          </div>
          <p style="font-size: 0.9rem; color: #666; margin-top: 10px;">Escanea este código para identificarte</p>
        `,
        showConfirmButton: true,
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#6c757d',
        showDenyButton: true,
        denyButtonText: '<i class="bi bi-printer-fill"></i> Imprimir',
        denyButtonColor: '#ff6b35',
        showCloseButton: true,
        customClass: {
          popup: 'swal-qr-code'
        }
      });

      if (result.isDenied) {
        this.printQR();
      }

    } catch (error) {
      console.error('Error generating QR code:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al generar el código QR.',
        confirmButtonColor: '#d33'
      });
    } finally {
      this.working = false;
    }
  }

  private printQR() {
    if (!this.qrCodeUrl || !this.clientData) return;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Imprimir Código QR</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background-color: #fff;
            }
            .print-container {
              text-align: center;
              padding: 40px;
              border: 2px solid #000;
              border-radius: 16px;
              max-width: 400px;
              width: 100%;
            }
            .farmacia-name {
              font-size: 24px;
              font-weight: 700;
              color: #000;
              margin-bottom: 8px;
              text-transform: uppercase;
              letter-spacing: -0.5px;
            }
            .qr-title {
              font-size: 16px;
              color: #555;
              font-weight: 500;
              margin-bottom: 24px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .qr-image {
              width: 100%;
              height: auto;
              display: block;
              margin: 0 auto;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; }
              .print-container { border: none; } /* Opcional: quitar borde al imprimir si se prefiere */
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="farmacia-name">${this.labName || this.clientData.razon_social || 'Droguería Suiza'}</div>
            <div class="qr-title">Código QR de Identificación</div>
            <img src="${this.qrCodeUrl}" class="qr-image" alt="QR Code" />
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  }



  private async handleLogout(e: Event) {
    e.preventDefault(); e.stopPropagation();
    if (this.working) return;
    this.working = true;
    try {
      const res = await Swal.fire(this.getLogoutSwalConfig());
      if (!res.isConfirmed) return;

      setSuppressLogoutAlert(true);
      resetAuthSystem(); // asegura que logoutInProgress no quede trancado de una sesión previa
      await logout(false, "logout manual");

    } finally { this.working = false; }
  }

  @state() private showDropdown = false;

  private toggleDropdown(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.showDropdown = !this.showDropdown;

    if (this.showDropdown) {
      const close = (ev: MouseEvent) => {
        if (!(ev.target as HTMLElement).closest('.ic')) {
          this.showDropdown = false;
          document.removeEventListener('click', close);
        }
      };
      document.addEventListener('click', close);
    }
  }

  render() {
    const lab = this.labName;
    return html`
      <style>
        .ic { display:flex; align-items:center; position: relative; }
        .ic .navbar-nav { margin:0; display:flex; align-items:center; }

        .ic .nav-link {
          font-weight: 600; font-size: .86rem; line-height: 1;
          padding: .42rem .6rem; color: var(--text, #1f2937);
          border-radius: 10px; transition: color .2s ease, background .2s ease;
          max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          text-transform: uppercase; letter-spacing: .3px;
          cursor: pointer;
        }
        .ic .nav-link:hover { color: var(--primary, #0bb1d5); background: rgba(11,177,213,.08); }

        .ic .dropdown-menu {
          display: none;
          position: absolute;
          top: 100%;
          right: 0;
          font-size: .88rem; border: none; border-radius: 10px;
          box-shadow: 0 10px 28px rgba(0,0,0,.14);
          padding: .4rem; min-width: 230px;
          background: var(--bg-strong, #fff);
          z-index: 1000;
          margin-top: 5px;
        }
        .ic .dropdown-menu.show {
          display: block;
        }

        .ic .dropdown-item {
          padding: .55rem .8rem; border-radius: 8px; font-weight: 500;
          cursor: pointer; background: none; border: none; width: 100%; text-align: left;
          color: var(--text, #1f2937);
          display: block;
          text-decoration: none;
        }
        .ic .dropdown-item:hover { background: rgba(255, 107, 53, 0.2); color: #1f2937 !important; }
        .ic .dropdown-divider { margin: .35rem 0; border-color: rgba(203,213,225,.6); }

        /* 🎨 Modo oscuro completo */
        [data-theme="dark"] .ic .nav-link { color: var(--text, #f9fafb); }
        [data-theme="dark"] .ic .dropdown-menu {
          background: rgba(31,41,55,0.96) !important;
          border: 1px solid rgba(75,85,99,0.45) !important;
          box-shadow: 0 10px 28px rgba(0,0,0,0.55) !important;
        }
        [data-theme="dark"] .ic .dropdown-item {
          color: #e5e7eb !important;
          background: transparent !important;
        }
        [data-theme="dark"] .ic .dropdown-item:hover {
          background: rgba(255, 107, 53, 0.2) !important;
          color: #e5e7eb !important;
        }
        [data-theme="dark"] .ic .dropdown-divider {
          border-color: rgba(75,85,99,0.5);
        }

        /* MODO ICONO */
        :host([icononly]) .nav-link{
          width:42px;height:42px;display:grid;place-items:center;padding:.35rem;
          border-radius:50%; border:2px solid #ff6b35;
          text-transform:none; letter-spacing:0; max-width:none;
        }
        :host([icononly]) .nav-link:hover{ background: rgba(11,177,213,.1); }
        :host([icononly]) .nav-link svg{ width:20px;height:20px;color:#ff6b35;fill:#ff6b35; }

        /* MODO EXPANDIDO */
        :host([expanded]) .nav-link {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--bg-strong, rgba(255, 255, 255, 0.98));
          border: var(--bd, 1px solid rgba(226, 232, 240, 0.6));
          border-radius: var(--radius, 12px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          text-transform: none;
          letter-spacing: 0;
          max-width: none;
          font-weight: 500;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :host([expanded]) .nav-link:hover {
          background: rgba(255, 255, 255, 1);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          transform: translateY(-1px);
          border-color: var(--primary);
        }
        :host([expanded]) .nav-link svg {
          width: 20px;
          height: 20px;
          color: #ff6b35;
          fill: #ff6b35;
          flex-shrink: 0;
        }

        /* Dropdown correcto dentro del offcanvas (mobile) */
        .offcanvas .ic{ position:relative; }
        .offcanvas .ic .dropdown-menu{
          position:absolute;
          top: calc(100% + 12px);
          right: 0;
          left: auto;
          transform: none;
          z-index: 100000;
          max-width: min(260px, calc(100vw - 40px));
        }
      </style>

      <div class="ic" title="${this.usuario}">
        <ul class="navbar-nav">
          <li class="nav-item dropdown">
            <a class="nav-link dropdown-toggle"
               href="#"
               @click=${this.toggleDropdown}
               aria-expanded="${this.showDropdown}">
              ${this.icononly
        ? html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ff6b35">
                        <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm7 9a7 7 0 00-14 0h14z"/></svg>`
        : this.expanded
          ? html`
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ff6b35">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                    <span>${this.usuario}</span>
                  `
          : html`
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ff6b35" style="width: 18px; height: 18px; margin-right: 0.3rem;">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                    <span>${this.usuario}</span>
                  `}
            </a>

            <ul class="dropdown-menu ${this.showDropdown ? 'show' : ''}">
              ${this.isClienteAdmin ? html`
              <li>
                <button type="button" class="dropdown-item" @click=${() => { this.showDropdown = false; window.location.hash = "#/clients/mis-ajustes"; }}>
                  <i class="bi bi-gear-fill me-1"></i> Mis Ajustes
                </button>
              </li>
              ${this.hasQR ? html`
              <li>
                <button type="button" class="dropdown-item" @click=${() => { this.showDropdown = false; this.handleShowQR(); }}>
                  <i class="bi bi-qr-code me-1"></i> Código QR Para Logística
                </button>
              </li>
              `: ''}
              <li><hr class="dropdown-divider" /></li>
              ` : ''}
              <li>
                <button type="button"
                        class="dropdown-item"
                        ?disabled=${this.working}
                        @click=${(e: Event) => { this.showDropdown = false; this.handleLogout(e); }}>
                  <i class="bi bi-box-arrow-right me-1"></i> Cerrar sesión
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    `;
  }
}

if (!customElements.get("info-cliente")) {
  customElements.define("info-cliente", InfoCliente);
}
